import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import turmasService from '../services/turmasService';
import { seriesService } from '../services/seriesService';
import type { Serie } from '../services/seriesService';
import type { TurmaResponse, CreateTurmaDto, UpdateTurmaDto, TurnoEnum } from '../services/turmasService';

interface TurmaFormModalProps {
  turma?: TurmaResponse | null;
  onSuccess: () => void;
  onClose: () => void;
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #eee;
`;

const Title = styled.h2`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const Content = styled.div`
  padding: 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &:disabled {
    background: #f5f5f5;
    color: #666;
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &:disabled {
    background: #f5f5f5;
    color: #666;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #eee;
  margin-top: 20px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
  
  ${props => props.variant === 'primary' ? `
    background: #007bff;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  ` : `
    background: #f8f9fa;
    color: #666;
    border: 1px solid #ddd;
    
    &:hover {
      background: #e9ecef;
    }
  `}
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
`;

const TurmaFormModal: React.FC<TurmaFormModalProps> = ({ turma, onSuccess, onClose }) => {
  const [formData, setFormData] = useState<CreateTurmaDto & { ativa: boolean }>({
    nome: '',
    serieId: 0,
    turno: 'matutino' as TurnoEnum,
    ano: new Date().getFullYear(),
    valorMensalidade: 0,
    ativa: true
  });
  const [loading, setLoading] = useState(false);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [series, setSeries] = useState<Serie[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = Boolean(turma);

  useEffect(() => {
    if (turma) {
      setFormData({
        nome: turma.nome,
        serieId: turma.serieId ?? 0,
        turno: turma.turno,
        ano: turma.ano,
        valorMensalidade: turma.valorMensalidade,
        ativa: turma.ativa
      });
    }
  }, [turma]);

  useEffect(() => {
    const loadSeries = async () => {
      try {
        setLoadingSeries(true);
        const data = await seriesService.getAll();
        setSeries(data);
      } catch (error) {
        console.error('Erro ao carregar séries:', error);
        toast.error('Erro ao carregar séries para o formulário');
      } finally {
        setLoadingSeries(false);
      }
    };

    loadSeries();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.serieId || formData.serieId <= 0) {
      newErrors.serie = 'Série é obrigatória';
    }

    if (!formData.turno) {
      newErrors.turno = 'Turno é obrigatório';
    }

    if (!formData.ano || formData.ano < 2020 || formData.ano > 2030) {
      newErrors.ano = 'Ano deve estar entre 2020 e 2030';
    }

    if (!formData.valorMensalidade || formData.valorMensalidade <= 0) {
      newErrors.valorMensalidade = 'Valor da mensalidade deve ser maior que zero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  type FormKey = keyof (CreateTurmaDto & { ativa: boolean });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (isEditing && turma) {
        const updateData: UpdateTurmaDto = { ...formData };
        await turmasService.update(turma.id, updateData);
        toast.success('Turma atualizada com sucesso');
      } else {
        await turmasService.create(formData as CreateTurmaDto);
        toast.success('Turma criada com sucesso');
      }

      onSuccess();
    } catch (error: any) {
      console.error('Erro ao salvar turma:', error);
      if (error.response?.status === 409) {
        toast.error('Já existe uma turma com este nome, série, turno e ano');
      } else {
        toast.error('Erro ao salvar turma');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: FormKey, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSerieChange = (serieIdRaw: string) => {
    const serieId = serieIdRaw ? Number(serieIdRaw) : 0;
    const selectedSerie = series.find(item => item.id === serieId);
    setFormData(prev => ({
      ...prev,
      serieId,
      valorMensalidade: selectedSerie ? selectedSerie.valorMensalidade : prev.valorMensalidade,
    }));

    if (errors.serie) {
      setErrors(prev => ({ ...prev, serie: '' }));
    }
  };

  const selectedSerie = series.find((s) => s.id === formData.serieId);

  const seriesOptions = isEditing
    ? series.filter(item => item.ativa || item.id === formData.serieId)
    : series.filter(item => item.ativa);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <Modal>
        <Header>
          <Title>{isEditing ? 'Editar Turma' : 'Nova Turma'}</Title>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        <Content>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="nome">Nome da Turma *</Label>
              <Input
                id="nome"
                type="text"
                value={formData.nome}
                onChange={(e) => handleChange('nome', e.target.value)}
                placeholder="Ex: 1º Ano A"
                disabled={loading}
              />
              {errors.nome && <ErrorMessage>{errors.nome}</ErrorMessage>}
            </FormGroup>

            <Row>
              <FormGroup>
                <Label htmlFor="serie">Série *</Label>
                <Select
                  id="serie"
                  value={formData.serieId ? String(formData.serieId) : ''}
                  onChange={(e) => handleSerieChange(e.target.value)}
                  disabled={loading || loadingSeries}
                >
                  <option value="">{loadingSeries ? 'Carregando séries...' : 'Selecione uma série'}</option>
                  {seriesOptions.map((serie) => (
                    <option key={serie.id} value={serie.id}>
                      {serie.nome} ({serie.tipoEnsino === 'curricular' ? 'Curricular' : 'Extracurricular'})
                    </option>
                  ))}
                </Select>
                {errors.serie && <ErrorMessage>{errors.serie}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="turno">Turno *</Label>
                <Select
                  id="turno"
                  value={formData.turno}
                  onChange={(e) => handleChange('turno', e.target.value as TurnoEnum)}
                  disabled={loading}
                >
                  <option value="matutino">Matutino</option>
                  <option value="vespertino">Vespertino</option>
                  <option value="noturno">Noturno</option>
                  <option value="integral">Integral</option>
                </Select>
                {errors.turno && <ErrorMessage>{errors.turno}</ErrorMessage>}
              </FormGroup>
            </Row>

            <FormGroup>
              <Label>Código de Serviço (NFSe)</Label>
              <Input
                value={
                  selectedSerie?.codigoServico
                    ? `${selectedSerie.codigoServico.codigoAtividade} (${selectedSerie.codigoServico.codigoServico}) - ${selectedSerie.codigoServico.descricao}`
                    : 'Nao definido na série'
                }
                disabled
              />
            </FormGroup>

            <Row>
              <FormGroup>
                <Label htmlFor="ano">Ano *</Label>
                <Input
                  id="ano"
                  type="number"
                  min="2020"
                  max="2030"
                  value={formData.ano}
                  onChange={(e) => handleChange('ano', parseInt(e.target.value))}
                  disabled={loading}
                />
                {errors.ano && <ErrorMessage>{errors.ano}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="valorMensalidade">Valor da Mensalidade *</Label>
                <Input
                  id="valorMensalidade"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.valorMensalidade}
                  onChange={(e) => handleChange('valorMensalidade', parseFloat(e.target.value))}
                  placeholder="0,00"
                  disabled={loading || loadingSeries}
                />
                {errors.valorMensalidade && <ErrorMessage>{errors.valorMensalidade}</ErrorMessage>}
              </FormGroup>
            </Row>

            <CheckboxContainer>
              <Checkbox
                id="ativa"
                type="checkbox"
                checked={formData.ativa}
                onChange={(e) => handleChange('ativa', e.target.checked)}
                disabled={loading}
              />
              <Label htmlFor="ativa">Turma ativa</Label>
            </CheckboxContainer>

            <ButtonContainer>
              <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
              </Button>
            </ButtonContainer>
          </Form>
        </Content>
      </Modal>
    </Overlay>
  );
};

export default TurmaFormModal;
